import { apiClient, v3Client } from '@api/apiClient';
import { V4ApiResponse } from '@api/types';
import { CHANGE_TRACKING_SOURCES } from '@changeTracking/utils/changeTrackingRegistry';
import { buildChangeTrackingRequestConfig } from '@changeTracking/utils/buildChangeTrackingHeaders';
import { ToastType } from '@components';
import { FeatureFlags } from '@constants/featureFlags';
import { isFeatureFlagEnabled } from '@store/featureFlags/store';
import {
  ATGDormantPoolDataType,
  DormantPoolGridQueryParamsType,
  DormantPoolListApiResponseType,
  DormantPoolListType,
} from '@typeDefinitions/dormantPoolTypes';
import { createError } from '@utils/errorUtils';
import { handleToast } from '@utils/utils';

const isPortRemainingV3ApisEnabled = isFeatureFlagEnabled(
  FeatureFlags.PortRemainingV3Apis,
);

export const getDormantPoolListApi = async (
  payload: Record<string, unknown>,
  hideNotInterested: boolean,
) => {
  const url = 'DormantCustomer/dormantcustomerslist';
  try {
    const response: V4ApiResponse<DormantPoolListApiResponseType> =
      await apiClient.post(url, payload, { params: { hideNotInterested } });
    if (response.isSuccess) {
      return response.value;
    }
  } catch (e) {
    const error = createError(e, 'Unable to fetch dormant customer data');
    handleToast(ToastType.Error, error.message);
  }
};

export const takeCustomerApi = async (id: string) => {
  try {
    const url = 'DormantCustomer/claimdormantcustomer';
    const response: V4ApiResponse<unknown> = await apiClient.post(
      url,
      { id },
      buildChangeTrackingRequestConfig([
        CHANGE_TRACKING_SOURCES.dormantCustomers.takeCustomer,
      ]),
    );
    if (response.isSuccess) {
      return response.isSuccess;
    }
  } catch (e) {
    const error = createError(e, 'Unable to take customer');
    handleToast(ToastType.Error, error.message);
  }
};

export const markCustomerNotInterestedApi = async (
  id: string,
  status: boolean,
) => {
  try {
    const url = 'DormantCustomer/toggleintereststatus';
    const response: V4ApiResponse<unknown> = await apiClient.post(url, null, {
      params: { id, status: !status },
      ...buildChangeTrackingRequestConfig([
        CHANGE_TRACKING_SOURCES.dormantCustomers.toggleInterestStatus,
      ]),
    });
    if (response.isSuccess) {
      return response.isSuccess;
    }
  } catch (e) {
    const action = status ? 'interested' : 'not interested';
    const error = createError(e, `Unable to mark customer as ${action}`);
    handleToast(ToastType.Error, error.message);
  }
};

export const getClaimedCustomersApi = async () => {
  try {
    const url = 'DormantCustomer/claimed';
    const response: V4ApiResponse<DormantPoolListType[]> =
      await apiClient.get(url);
    if (response.isSuccess) {
      return response.value;
    }
  } catch (e) {
    const error = createError(e, 'Unable to fetch claimed customers data');
    handleToast(ToastType.Error, error.message);
  }
};

export const getATGDormantPoolDataApi = async () => {
  try {
    if (isPortRemainingV3ApisEnabled()) {
      const response: V4ApiResponse<ATGDormantPoolDataType> =
        await apiClient.get('/Data');
      if (!response.isSuccess) {
        handleToast(
          ToastType.Error,
          response.errors[0]?.message ??
            'Failed to get roles and permissions info',
        );
        return;
      }
      return response.value;
    }

    const response: unknown = await v3Client.get('/Data');
    return response as ATGDormantPoolDataType;
  } catch (e: unknown) {
    const error = createError(e, 'Failed to get roles and permissions info');
    handleToast(ToastType.Error, error.message);
  }
};

export const deleteClaimedCustomerApi = async (payload: {
  id: string;
  comment: string;
}) => {
  try {
    const url = 'DormantCustomer';
    const response: V4ApiResponse<boolean> = await apiClient.delete(url, {
      data: payload,
      ...buildChangeTrackingRequestConfig([
        CHANGE_TRACKING_SOURCES.dormantCustomers.removeClaimedCustomer,
      ]),
    });
    if (response.isSuccess) {
      return response.isSuccess;
    }
  } catch (e: unknown) {
    const error = createError(e, 'Failed to delete selected claimed customer');
    handleToast(ToastType.Error, error.message);
  }
};

export const getDormantPoolGridApi = async (
  payload: DormantPoolGridQueryParamsType,
) => {
  try {
    const url = '/DormantCustomer/Search';
    const response: V4ApiResponse<DormantPoolListType[]> = await apiClient.post(
      url,
      payload,
    );
    if (response.isSuccess) {
      return response.value;
    }
  } catch (e) {
    const error = createError(e, 'Unable to fetch dormant customer data');
    handleToast(ToastType.Error, error.message);
  }
};

export const getPoolSizeApi = async () => {
  try {
    const url = '/DormantCustomer/poolsize';
    const response: V4ApiResponse<number> = await apiClient.get(url);
    if (response.isSuccess) {
      return response.value;
    }
  } catch (e) {
    const error = createError(e, 'Unable to fetch dormant pool size');
    handleToast(ToastType.Error, error.message);
  }
};
